import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import { useSelector } from "react-redux";
import { getTenantId } from "../../../redux/features/auth/authSlice";
import { useLazyGetAllTenantProductsQuery } from "../../../redux/services/productApiSlice";
import { useGetTrackNTraceReceiveAsnQuery } from "../../../redux/services/trackntraceApiSlice";
import { removeEmptyKeys } from "../../../utils";
import { useTenantSitesAndZones } from "../../../hooks/useTenantSitesAndZones";
import { UICalendar } from "../../common/uiCalendar";
import { Page } from "../../../routes/config";
import type { SiteReceiveReportRow } from "./asnReportHelpers";
import ReportFiltersCard from "./ReportFiltersCard";
import { REPORT_TABLE_BODY_MAX_PX } from "./reportTableLayout";
import "./style.css";
import {
  extendTrail,
  makeTrailItem,
  writeReportTrailState,
} from "./reportTrail";
import { exportRowsToWorkbook } from "./exportWorkbook";
import { UIAgGridColumn, UIAgGridTable } from "../../common/agGridTable";
import { ReportToolbar, ReportFilterChips } from "../../common/reportToolbar";
import Select from "../../common/selectInput/Select";
import Input from "../../common/input/Input";
import { Pagination } from "../../common/pagination";
import { MetricSkeleton } from "./components/MetricSkeleton";
import { MetricCard } from "./components/MetricCard";
import { AlertTriangle, BarcodeIcon, Layers, MapPin, PackageCheck, PackageOpen, PackageX, X } from "lucide-react";

type ReceiveSkuRow = SiteReceiveReportRow & {
  processName?: string;
  lastProcessSession?: string;
  receivedProcessSession?: string;
};

const SiteReceiveReport = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const tenantId = useSelector(getTenantId);
  const {
    zoneRows,
    siteNameById,
    zoneNameById,
    siteOptionsById,
    zoneOptionsById,
  } = useTenantSitesAndZones(tenantId);

  const skuFromQuery = searchParams.get("sku") ?? "";
  const asnFromQuery = searchParams.get("asn") ?? "";
  const siteIdFromQuery = searchParams.get("siteId") ?? "";
  const fromFromQuery = searchParams.get("from") ?? "";
  const toFromQuery = searchParams.get("to") ?? "";

  const [zoneId, setZoneId] = useState<string | undefined>(undefined);
  const [siteId, setSiteId] = useState<string | undefined>(undefined);
  const [asn, setAsn] = useState("");
  const [dateRange, setDateRange] = useState<moment.Moment[]>([]);
  const [appliedZoneId, setAppliedZoneId] = useState<string | undefined>(
    undefined,
  );
  const [appliedSiteId, setAppliedSiteId] = useState<string | undefined>(
    undefined,
  );
  const [appliedAsn, setAppliedAsn] = useState("");
  const [appliedDateRange, setAppliedDateRange] = useState<moment.Moment[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortConfig, setSortConfig] = useState<{
    field?: keyof SiteReceiveReportRow;
    order?: "ascend" | "descend";
  }>({});
  const [triggerGetProducts] = useLazyGetAllTenantProductsQuery();
  const [productDescriptionBySku, setProductDescriptionBySku] = useState<
    Record<string, string>
  >({});

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const siteReceiveQueryParams = useMemo(
    () =>
      removeEmptyKeys({
        tenantId: tenantId || undefined,
        reportType: "receive",
        page: 1,
        limit: 10000,
        sku: skuFromQuery.trim() || undefined,
        siteId: siteIdFromQuery.trim() || undefined,
        from:
          appliedDateRange.length > 0
            ? moment.utc(appliedDateRange[0]).format("YYYY-MM-DD")
            : fromFromQuery || undefined,
        to:
          appliedDateRange.length > 0
            ? moment.utc(appliedDateRange[1]).format("YYYY-MM-DD")
            : toFromQuery || undefined,
      }),
    [
      tenantId,
      skuFromQuery,
      siteIdFromQuery,
      appliedDateRange,
      fromFromQuery,
      toFromQuery,
    ],
  );

  const { data: receiveData, isFetching } = useGetTrackNTraceReceiveAsnQuery(
    siteReceiveQueryParams,
    {
      skip: !tenantId,
    },
  );

  const receiveResult = useMemo(
    () => receiveData?.result ?? receiveData,
    [receiveData],
  );

  const uniqueSkus = useMemo(() => {
    const sourceRows = (receiveResult?.rows ??
      receiveResult?.data ??
      []) as any[];
    const skus = new Set<string>();
    sourceRows.forEach((parent) => {
      const nestedSkus = Array.isArray(parent?.skus) ? parent.skus : [];
      nestedSkus.forEach((s: any) => {
        const sku = String(s?.sku ?? s?.productUPC ?? "").trim();
        if (sku) skus.add(sku);
      });
    });
    return Array.from(skus);
  }, [receiveResult]);

  useEffect(() => {
    let mounted = true;
    const fetchProductDescriptions = async () => {
      if (!tenantId || uniqueSkus.length === 0) {
        if (mounted) setProductDescriptionBySku({});
        return;
      }

      const entries = await Promise.all(
        uniqueSkus.map(async (sku) => {
          try {
            const response: any = await triggerGetProducts(
              { tenantId, upc: sku },
              true,
            );
            const payload = response?.data?.result ?? response?.data ?? {};
            const products = Array.isArray(payload?.records)
              ? payload.records
              : Array.isArray(payload?.rows)
                ? payload.rows
                : Array.isArray(payload)
                  ? payload
                  : [];
            const matchingProduct =
              products.find(
                (p: any) =>
                  String(p?.upc ?? p?.UPC ?? p?.sku ?? p?.skuUpc ?? "")
                    .trim()
                    .toLowerCase() === sku.toLowerCase(),
              ) ?? products[0];
            const description = String(
              matchingProduct?.description ??
                matchingProduct?.productDescription ??
                matchingProduct?.name ??
                "",
            ).trim();
            return [sku, description] as const;
          } catch {
            return [sku, ""] as const;
          }
        }),
      );
      if (!mounted) return;
      setProductDescriptionBySku(Object.fromEntries(entries));
    };
    fetchProductDescriptions();
    return () => {
      mounted = false;
    };
  }, [tenantId, uniqueSkus, triggerGetProducts]);

  const rows = useMemo<ReceiveSkuRow[]>(() => {
    const sourceRows = (receiveResult?.rows ??
      receiveResult?.data ??
      []) as any[];
    const grouped = new Map<string, ReceiveSkuRow>();
    sourceRows.forEach((parent, pIdx) => {
      if (!parent || typeof parent !== "object") return;
      const parentAsn = String(
        parent.asn ?? parent.asnNumber ?? parent.asnId ?? "",
      ).trim();
      const parentSiteName = String(
        parent.siteName ?? parent.site ?? "",
      ).trim();
      const parentZoneName = String(
        parent.zoneName ?? parent.zone ?? "",
      ).trim();
      const processName = String(
        parent.processName ?? "Receive ASN/Blind",
      ).trim();
      const receivedProcessSession = String(
        parent.receivedProcessSession ?? "",
      ).trim();
      const lastProcessSession = String(
        parent.lastProcessSession ?? receivedProcessSession ?? "",
      ).trim();
      const nestedSkus = Array.isArray(parent.skus) ? parent.skus : [];
      nestedSkus.forEach((s: any, sIdx: number) => {
        if (!s || typeof s !== "object") return;
        const sku = String(s.sku ?? s.productUPC ?? "").trim();
        if (!sku) return;
        const description = String(
          s.description ?? s.productDescription ?? "",
        ).trim();
        const key = `${sku}|${lastProcessSession || receivedProcessSession || "no-session"}|${parentSiteName}|${parentZoneName}`;
        const existing = grouped.get(key);
        if (existing) {
          existing.expectedQty += Number(
            s.expectedQty ?? s.expected ?? s.expectedCount ?? 0,
          );
          existing.receivedQty += Number(
            s.receivedQty ?? s.received ?? s.receivedCount ?? 0,
          );
          existing.missingQty += Number(
            s.missingQty ?? s.missing ?? s.missingCount ?? 0,
          );
          existing.unexpectedQty += Number(
            s.unexpectedQty ?? s.unexpected ?? s.unexpectedCount ?? 0,
          );
          if (!existing.description && description)
            existing.description = description;
          return;
        }
        grouped.set(key, {
          key: `sku-${pIdx}-${sIdx}`,
          asn: parentAsn,
          siteName: parentSiteName,
          zoneName: parentZoneName,
          userName: "",
          sku,
          description,
          expectedQty: Number(
            s.expectedQty ?? s.expected ?? s.expectedCount ?? 0,
          ),
          receivedQty: Number(
            s.receivedQty ?? s.received ?? s.receivedCount ?? 0,
          ),
          missingQty: Number(s.missingQty ?? s.missing ?? s.missingCount ?? 0),
          unexpectedQty: Number(
            s.unexpectedQty ?? s.unexpected ?? s.unexpectedCount ?? 0,
          ),
          identityId: "",
          siteId: "",
          zoneId: "",
          processName,
          lastProcessSession,
          receivedProcessSession,
        });
      });
    });
    const apiRows = Array.from(grouped.values()).map((row) => {
      const productDescription = productDescriptionBySku[row.sku] ?? "";
      return {
        ...row,
        description: productDescription || row.sku,
      };
    });

    const siteName = appliedSiteId
      ? (siteNameById.get(appliedSiteId) ?? "")
      : "";
    const zoneName = appliedZoneId
      ? (zoneNameById.get(appliedZoneId) ?? "")
      : "";
    const normalizedSite = siteName.trim().toLowerCase();
    const normalizedZone = zoneName.trim().toLowerCase();
    const normalizedSku = appliedAsn.trim().toLowerCase();
    const queryAsnNorm = asnFromQuery.trim().toLowerCase();
    const shouldApplyAsnFilter =
      !!queryAsnNorm && queryAsnNorm !== "blind" && queryAsnNorm !== "(no asn)";
    return apiRows.filter((r) => {
      const skuMatch =
        (!skuFromQuery || r.sku === skuFromQuery) &&
        (!normalizedSku || r.sku.toLowerCase().includes(normalizedSku));
      const siteMatch =
        !normalizedSite ||
        r.siteName.toLowerCase() === normalizedSite ||
        r.siteName.toLowerCase().includes(normalizedSite);
      const zoneMatch =
        !normalizedZone ||
        r.zoneName.toLowerCase().replace(/\s+/g, "") ===
          normalizedZone.replace(/\s+/g, "");
      const queryAsnMatch =
        !shouldApplyAsnFilter || r.asn.toLowerCase().includes(queryAsnNorm);
      return skuMatch && siteMatch && zoneMatch && queryAsnMatch;
    });
  }, [
    receiveResult,
    productDescriptionBySku,
    skuFromQuery,
    asnFromQuery,
    appliedSiteId,
    appliedZoneId,
    appliedAsn,
    siteNameById,
    zoneNameById,
  ]);

  const metrics = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.expectedQty += Number(row.expectedQty || 0);
        acc.receivedQty += Number(row.receivedQty || 0);
        acc.missingQty += Number(row.missingQty || 0);
        acc.unexpectedQty += Number(row.unexpectedQty || 0);
        return acc;
      },
      {
        expectedQty: 0,
        receivedQty: 0,
        missingQty: 0,
        unexpectedQty: 0,
      },
    );
  }, [rows]);

  const sortedRows = useMemo(() => {
    if (!sortConfig.field || !sortConfig.order) return rows;
    const dir = sortConfig.order === "ascend" ? 1 : -1;
    const field = sortConfig.field;
    const numberFields: Array<keyof SiteReceiveReportRow> = [
      "expectedQty",
      "receivedQty",
      "missingQty",
      "unexpectedQty",
    ];
    return [...rows].sort((a, b) => {
      if (numberFields.includes(field))
        return (Number(a[field] ?? 0) - Number(b[field] ?? 0)) * dir;
      return String(a[field] ?? "").localeCompare(String(b[field] ?? "")) * dir;
    });
  }, [rows, sortConfig]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [
    skuFromQuery,
    asnFromQuery,
    appliedSiteId,
    appliedZoneId,
    appliedAsn,
    appliedDateRange,
  ]);

  const applyFilters = () => {
    setAppliedSiteId(siteId);
    setAppliedZoneId(zoneId);
    setAppliedAsn(asn);
    setAppliedDateRange(dateRange);
    setPage(1);
  };

  const applySearch = (value: string) => {
    const trimmed = value.trim();
    setAppliedAsn((prev) => {
      if (prev === trimmed) return prev;
      setPage(1);
      return trimmed;
    });
  };

  const resetFilters = () => {
    setSiteId(undefined);
    setZoneId(undefined);
    setAsn("");
    setDateRange([]);
    setAppliedSiteId(undefined);
    setAppliedZoneId(undefined);
    setAppliedAsn("");
    setAppliedDateRange([]);
    setPage(1);
    setPageSize(50);
  };

  const clearQueryParams = (keys: string[]) => {
    const params = new URLSearchParams(location.search);
    keys.forEach((key) => params.delete(key));
    const query = params.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ""}`, {
      replace: true,
      state: location.state,
    });
  };

  const clearAllFilters = () => {
    resetFilters();
    const keysToRemove = ["siteId", "sku", "asn", "from", "to"].filter((key) =>
      searchParams.has(key),
    );
    if (keysToRemove.length) clearQueryParams(keysToRemove);
  };

  const activeFilterChips = useMemo(() => {
    const chips: { label: string; onRemove: () => void }[] = [];

    const effectiveSiteId = appliedSiteId || siteIdFromQuery || undefined;
    if (effectiveSiteId) {
      chips.push({
        label: `Site: ${
          siteNameById.get(effectiveSiteId) ?? effectiveSiteId
        }`,
        onRemove: () => {
          setSiteId(undefined);
          setAppliedSiteId(undefined);
          setZoneId(undefined);
          setAppliedZoneId(undefined);
          if (siteIdFromQuery) clearQueryParams(["siteId"]);
          setPage(1);
        },
      });
    }

    if (appliedZoneId) {
      chips.push({
        label: `Zone: ${zoneNameById.get(appliedZoneId) ?? appliedZoneId}`,
        onRemove: () => {
          setZoneId(undefined);
          setAppliedZoneId(undefined);
          setPage(1);
        },
      });
    }

    const effectiveSku = appliedAsn || skuFromQuery || undefined;
    if (effectiveSku) {
      chips.push({
        label: `SKU/UPC: ${effectiveSku}`,
        onRemove: () => {
          setAsn("");
          setAppliedAsn("");
          if (skuFromQuery) clearQueryParams(["sku"]);
          setPage(1);
        },
      });
    }

    const queryAsnNorm = asnFromQuery.trim().toLowerCase();
    if (
      queryAsnNorm &&
      queryAsnNorm !== "blind" &&
      queryAsnNorm !== "(no asn)"
    ) {
      chips.push({
        label: `ASN: ${asnFromQuery}`,
        onRemove: () => clearQueryParams(["asn"]),
      });
    }

    const hasAppliedDateRange = appliedDateRange?.length === 2;
    const hasQueryDateRange =
      !hasAppliedDateRange && !!fromFromQuery && !!toFromQuery;
    if (hasAppliedDateRange || hasQueryDateRange) {
      const start = hasAppliedDateRange
        ? appliedDateRange[0]
        : moment(fromFromQuery);
      const end = hasAppliedDateRange
        ? appliedDateRange[1]
        : moment(toFromQuery);
      chips.push({
        label: `Date: ${moment(start).format("DD/MM/YYYY")} - ${moment(
          end,
        ).format("DD/MM/YYYY")}`,
        onRemove: () => {
          setDateRange([]);
          setAppliedDateRange([]);
          if (hasQueryDateRange) clearQueryParams(["from", "to"]);
          setPage(1);
        },
      });
    }

    return chips;
  }, [
    appliedSiteId,
    siteIdFromQuery,
    appliedZoneId,
    appliedAsn,
    skuFromQuery,
    asnFromQuery,
    appliedDateRange,
    fromFromQuery,
    toFromQuery,
    siteNameById,
    zoneNameById,
  ]);

  const columns = useMemo<UIAgGridColumn<SiteReceiveReportRow>[]>(() => {
    return [
      {
        key: "sku",
        title: "SKU/UPC",
        dataIndex: "sku",
        minWidth: 150,
        flex: 1,
        sorter: true,
        render: (value: unknown) => (value ? String(value) : "-"),
      },
      {
        key: "description",
        title: "Description",
        dataIndex: "description",
        minWidth: 200,
        flex: 2,
        sorter: true,
        render: (value: unknown) => (value ? String(value) : "-"),
      },
      {
        key: "expectedQty",
        title: "Expected QTY",
        dataIndex: "expectedQty",
        minWidth: 140,
        width: 140,
        sorter: true,
        render: (value: unknown) => (
          <div className="text-center w-full">{String(value ?? "0")}</div>
        ),
      },
      {
        key: "receivedQty",
        title: "Received QTY",
        dataIndex: "receivedQty",
        minWidth: 140,
        width: 140,
        sorter: true,
        render: (value: unknown) => (
          <div className="text-center w-full">{String(value ?? "0")}</div>
        ),
      },
      {
        key: "missingQty",
        title: "Missing QTY",
        dataIndex: "missingQty",
        minWidth: 140,
        width: 140,
        sorter: true,
        render: (value: unknown) => (
          <div className="text-center w-full">{String(value ?? "0")}</div>
        ),
      },
      {
        key: "unexpectedQty",
        title: "Unexpected QTY",
        dataIndex: "unexpectedQty",
        minWidth: 150,
        width: 150,
        sorter: true,
        render: (value: unknown) => (
          <div className="text-center w-full">{String(value ?? "0")}</div>
        ),
      },
    ];
  }, []);

  const handleRowClick = (record: ReceiveSkuRow) => {
    const params = new URLSearchParams();
    params.set("identityId", record.identityId);
    params.set("sku", record.sku);
    if (record.asn) params.set("asn", record.asn);
    params.set("source", "receive");
    params.set("processName", record.processName || "Receive ASN/Blind");
    if (record.lastProcessSession)
      params.set("lastProcessSession", record.lastProcessSession);
    if (record.receivedProcessSession)
      params.set("receivedProcessSession", record.receivedProcessSession);
    if (tenantId) params.set("tenantId", tenantId);
    if (siteIdFromQuery) params.set("siteId", siteIdFromQuery);
    if (fromFromQuery) params.set("from", fromFromQuery);
    if (toFromQuery) params.set("to", toFromQuery);
    const to = `${Page.REPORTS_RECEIVE_LINE_ITEMS}?${params.toString()}`;
    const nextLabel = record.sku
      ? `Tag Details (${record.sku})`
      : "Tag Details";
    const nextItem = makeTrailItem(nextLabel, to);
    const nextTrail = extendTrail(location, nextItem);
    navigate(to, { state: writeReportTrailState(location.state, nextTrail) });
  };

  const exportExcel = () => {
    const header = [
      "SKU/UPC",
      "Description",
      "Expected QTY",
      "Received QTY",
      "Missing QTY",
      "Unexpected QTY",
    ];
    const excelRows: Array<Array<string | number>> = [
      header,
      ...rows.map((row) => [
        row.sku,
        row.description,
        row.expectedQty,
        row.receivedQty,
        row.missingQty,
        row.unexpectedQty,
      ]),
    ];
    exportRowsToWorkbook(
      "site-receive-report.xlsx",
      "Site Receive Report",
      excelRows,
    );
  };

  return (
    <div className="shadow-card h-full flex flex-col min-h-0  rounded-md gap-0">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 shrink-0">
        {isFetching ? (
          <>
            <MetricSkeleton />
            <MetricSkeleton />
            <MetricSkeleton />
            <MetricSkeleton />
          </>
        ) : (
          <>
            <MetricCard
              label="EXPECTED ITEMS"
              value={metrics.expectedQty}
              accent="default"
              icon={<PackageOpen />}
            />

            <MetricCard
              label="RECEIVED ITEMS"
              value={metrics.receivedQty}
              accent="green"
              icon={<PackageCheck />}
            />

            <MetricCard
              label="MISSING ITEMS"
              value={metrics.missingQty}
              accent="red"
              icon={<PackageX />}
            />

            <MetricCard
              label="UNEXPECTED ITEMS"
              value={metrics.unexpectedQty}
              accent="orange"
              icon={<AlertTriangle />}
            />
          </>
        )}
      </div>
      <div className="mt-3">
        <ReportToolbar
          searchValue={asn}
          onSearchChange={setAsn}
          onDebounceSearch={applySearch}
          onSearchEnter={applySearch}
          searchPlaceholder="Search SKU/UPC"
          showAdvancedFilters={showAdvancedFilters}
          onToggleAdvancedFilters={() =>
            setShowAdvancedFilters((show) => !show)
          }
          onApplyFilters={applyFilters}
          onResetFilters={clearAllFilters}
          activeFilterCount={activeFilterChips.length}
          onExportExcel={exportExcel}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {/* Site */}
            <div className="w-full">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                Site
              </label>
              <Select
                placeholder="Select Site"
                value={siteId}
                onChange={(e) => {
                  const value = e.target.value;
                  setSiteId(value);
                  setZoneId("");
                }}
                onClear={() => {
                  setSiteId("");
                  setZoneId("");
                }}
                allowClear
                options={siteOptionsById}
              />
            </div>

            {/* Zone */}
            <div className="w-full">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                Zone
              </label>
              <Select
                placeholder="Select Zone"
                value={zoneId}
                onChange={(e) => {
                  const value = e.target.value;
                  setZoneId(value);

                  const z = zoneRows.find(
                    (r) => String(r.id) === String(value),
                  );

                  if (z?.siteId) {
                    setSiteId(String(z.siteId));
                  }
                }}
                onClear={() => setZoneId("")}
                allowClear
                options={zoneOptionsById}
              />
            </div>

            {/* Time Range */}
            <div className="w-full">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                Time Range
              </label>
              <UICalendar
                hideArrows
                params={dateRange}
                setParams={setDateRange}
                handleChange={(dates: moment.Moment[]) =>
                  setDateRange(dates || [])
                }
                className="w-full"
              />
            </div>
          </div>
        </ReportToolbar>
      </div>
      <ReportFilterChips chips={activeFilterChips} onClearAll={clearAllFilters} />
      <div className="table-container">
        <UIAgGridTable
          columns={columns}
          data={pagedRows}
          loading={isFetching}
          rowHeight={44}
          onRowClick={(record: ReceiveSkuRow) => {
            handleRowClick(record);
          }}
          onSortChange={(sort) => {
            setSortConfig({
              field:
                (sort.field as keyof SiteReceiveReportRow | undefined) ??
                undefined,
              order: sort.order ?? undefined,
            });
          }}
        />
        <Pagination
          current={page}
          pageSize={pageSize}
          className="rounded-b-md border-t border-slate-200 dark:border-gray-700"
          total={sortedRows.length}
          hideOnSinglePage={false}
          showSizeChanger={true}
          onPageChange={(p) => {
            setPage(p);
          }}
          onPageSizeChange={(newSize) => {
            setPage(1);
            setPageSize(newSize);
          }}
        />
      </div>
    </div>
  );
};

export default SiteReceiveReport;
